import React, { useEffect, useCallback, useRef } from "react";
import cx from "classnames";
import styles from "./Progress.module.scss";

interface Props {
  progress?: number | boolean;
  duration?: number;
  size?: "small" | "medium";
  callback?: () => void;
  abort?: () => void;
}

const colors: string[] = ["#71d7b5", "#398975"];

enum Status {
  RENDERED_SINGLE,
  RENDERED_ALL,
  ONGOING,
  ABORTED,
}

export const Progress: React.FunctionComponent<Props> = ({
  progress = false,
  duration = 3000,
  size = "small",
  callback,
  abort,
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const start = useRef<number>(undefined);
  const end = useRef<number>(undefined);
  const timer = useRef<number>(undefined);
  const aborted = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      aborted.current = true;
    };
  }, []);

  useEffect(() => {
    if (typeof progress === "number") {
      return;
    }

    if (progress) {
      aborted.current = false;
    } else {
      aborted.current = true;
    }
  }, [progress]);

  const drawProgress = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
      progress: number | boolean
    ): Status => {
      let degrees = 0;
      const isStatic = typeof progress === "number";
      if (isStatic) {
        degrees = (360 * progress) / 100;
      } else if (aborted.current) {
        return Status.ABORTED;
      } else {
        const diff = new Date().getTime() - (start.current || 0);
        const progress = diff / ((end.current || 0) - (start.current || 0));
        degrees = 360 * progress;
      }

      const d = width * 0.6;
      ctx.clearRect(0, 0, width, height);
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, d / 2, 0, Math.PI * 2, true);
      ctx.strokeStyle = colors[0];
      ctx.lineWidth = width / 5;
      ctx.stroke();

      ctx.beginPath();
      ctx.strokeStyle = colors[1];
      ctx.lineWidth = width / 5;
      ctx.arc(
        width / 2,
        height / 2,
        d / 2,
        (Math.PI / 180) * 270,
        (Math.PI / 180) * (270 + degrees)
      );
      ctx.stroke();

      if (isStatic) {
        return Status.RENDERED_SINGLE;
      }

      if (degrees >= 360) {
        return Status.RENDERED_ALL;
      }

      return Status.ONGOING;
    },
    []
  );

  const runAnimation = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
      progress: number | boolean
    ) => {
      window.requestAnimationFrame(() => {
        const status = drawProgress(ctx, width, height, progress);

        if (status === Status.ABORTED) {
          if (!abort) {
            return;
          }

          abort();
          return;
        }

        if (status === Status.RENDERED_ALL) {
          if (!callback) {
            return;
          }

          callback();
          return;
        }

        if (status === Status.RENDERED_SINGLE) {
          if (!callback) {
            return;
          }

          callback();
          return;
        }

        runAnimation(ctx, width, height, progress);
      });
    },
    [drawProgress, callback, abort]
  );

  const draw = useCallback(
    (progress: number | boolean, duration: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const scale = window.devicePixelRatio;

      const box = canvas.getBoundingClientRect();
      canvas.width = box.width * scale;
      canvas.height = box.height * scale;

      if (progress === false) {
        return;
      }

      start.current = new Date().getTime();
      end.current = new Date(start.current + duration).getTime();
      runAnimation(ctx, canvas.width, canvas.height, progress);
    },
    [runAnimation]
  );

  useEffect(() => {
    clearTimeout(timer.current);

    timer.current = window.setTimeout(() => {
      draw(progress, duration);
    }, 100);
  }, [draw, duration, progress]);

  return (
    <canvas ref={canvasRef} className={cx(styles.canvas, [styles[size]])} />
  );
};
